import { useForm } from 'react-hook-form';

import { Stack } from '@mui/material';

import { useSnackbar, VALIDATION_RULES } from '@localstack/services';
import {
  PatchOrganizationRequest,
  PatchOrganizationResponse,
  OrganizationBillingDetails,
  OrganizationBasicDetails,
} from '@localstack/types';

import {
  ButtonWithDialog,
  ContentModalActions,
  ControlledCountryAutocomplete,
  ControlledPhoneField,
  ControlledTextField,
} from '@localstack/ui';

interface OrgDetailsDialogProps {
  basicDetails?: OrganizationBasicDetails;
  billingDetails?: OrganizationBillingDetails;
  /** Handler for when the form is submitted returns whether the submission was successful */
  onSubmit: (data: PatchOrganizationRequest) => Promise<PatchOrganizationResponse>;
  /** Whether the organization is currently being updated */
  isUpdatingOrg: boolean;
}

export const OrgDetailsDialog = ({
  onSubmit,
  isUpdatingOrg,
  basicDetails,
  billingDetails,
}: OrgDetailsDialogProps): JSX.Element => {
  const defaultValues = { basic_details: basicDetails, billing_details: billingDetails };
  const { control, handleSubmit, reset } = useForm<PatchOrganizationRequest>({ mode: 'all', defaultValues });
  const { showSnackbar } = useSnackbar();

  const onFormSubmit = async (data: PatchOrganizationRequest, closeDialog: () => void) => {
    const didSubmitSuccessfully = await onSubmit(data);

    if (didSubmitSuccessfully) {
      showSnackbar({
        message: 'Billing details identifier updated',
        severity: 'success',
      });
      closeDialog();
    } else {
      showSnackbar({
        message: 'Error updating billing details',
        severity: 'error',
      });
    }
  };

  return (
    <ButtonWithDialog
      componentType="Button"
      size="small"
      style={{ justifySelf: 'end', minWidth: 'fit-content', padding: '4px 10px' }}
      dialogTitle="Update billing details"
      onBeforeShow={() => reset(defaultValues)}
      hideDialogActions
      dialogContent={(closeDialog) => (
        <form onSubmit={handleSubmit((data) => onFormSubmit(data, closeDialog))}>
          <Stack spacing={2} pt={1}>
            <ControlledTextField
              control={control}
              required
              name="billing_details.email"
              size="small"
              label="Billing email"
              placeholder="Billing email"
              fullWidth
              variant="outlined"
              rules={VALIDATION_RULES.email}
            />
            <ControlledTextField
              control={control}
              required
              size="small"
              name="basic_details.name"
              label="Organization name"
              placeholder="Organization name"
              fullWidth
              variant="outlined"
              rules={VALIDATION_RULES.safeStringRequired}
            />
            <ControlledPhoneField
              control={control}
              size="small"
              required
              name="billing_details.phone"
              label="Primary phone"
              placeholder="Primary phone"
              fullWidth
              variant="outlined"
              rules={VALIDATION_RULES.phoneRequired}
            />
            <ControlledCountryAutocomplete
              control={control}
              // @ts-expect-error the rest props are passed to both autocomplete and the controller resulting in an error since size is not a property of both. With more time we can go back and fix this properly.
              size="small"
              name="billing_details.country"
              rules={VALIDATION_RULES.required}
              inputProps={{
                label: 'Country',
                required: true,
                placeholder: 'Country',
                fullWidth: true,
                variant: 'outlined',
              }}
            />
            <ControlledTextField
              control={control}
              name="billing_details.address"
              size="small"
              label="Address"
              placeholder="Address"
              required
              fullWidth
              variant="outlined"
              rules={VALIDATION_RULES.safeStringRequired}
            />
            <ControlledTextField
              control={control}
              name="billing_details.state"
              size="small"
              label="State"
              placeholder="State"
              fullWidth
              variant="outlined"
              rules={VALIDATION_RULES.safeStringOptional}
            />
            <ControlledTextField
              control={control}
              size="small"
              required
              name="billing_details.zip"
              label="Postal code"
              placeholder="Postal code"
              fullWidth
              variant="outlined"
              rules={{ ...VALIDATION_RULES.safeStringRequired, ...VALIDATION_RULES.zipCode }}
            />
            <ControlledTextField
              control={control}
              size="small"
              required
              name="billing_details.city"
              label="City"
              placeholder="City"
              fullWidth
              variant="outlined"
              rules={VALIDATION_RULES.safeStringRequired}
            />
            <ContentModalActions
              onClose={closeDialog}
              confirmButtonText="Update billing details"
              confirmButtonProps={{
                loading: isUpdatingOrg,
                type: 'submit',
              }}
            />
          </Stack>
        </form>
      )}
    >
      Edit
    </ButtonWithDialog>
  );
};
